import { Box, GlobalStyles } from '@mui/material';
import { trace } from '@opentelemetry/api';
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
  AppLoadContext,
} from '@remix-run/cloudflare';
import {
  redirect,
  useLocation,
  useActionData,
  useRouteError,
} from '@remix-run/react';

import { COLORS } from '@tbd/life-tokens';
import { createLogger, getCookieDomain } from '@tbd/lift-portal-shared-config';
import { OTEL_NAMES } from '@tbd/lift-portal-shared-config/tracing';

import { SignIn } from '#app/features/SignIn.js';
import type { SignInProps } from '#app/features/SignIn.js';

type PortalService = AppLoadContext['cloudflare']['env']['PORTAL_SERVICE'];

const routeLogger = createLogger([
  'lift-portal',
  'route',
  'anonymous',
  'login',
]);

export const meta: MetaFunction = () => {
  return [{ title: 'LIFT Portal - Login' }];
};

export const loader = async ({ context }: LoaderFunctionArgs) => {
  const tracer = trace.getTracer(OTEL_NAMES.ROUTE_TRACER);

  return tracer.startActiveSpan('login-loader', async (span) => {
    try {
      span.setAttribute('route', 'anonymous.login');
      const logger = routeLogger.child('loader');

      if (context.user) {
        logger.debug('redirecting to home page');
        span.setAttribute('result', 'already-logged-in');
        span.end();
        throw redirect('/');
      }

      span.setAttribute('result', 'show-login-form');
      span.end();
      return { data: null, error: null };
    } catch (error) {
      // Don't add tracing for redirect errors as they're expected flow
      if (
        !(
          error instanceof Response &&
          error.status >= 300 &&
          error.status < 400
        )
      ) {
        span.recordException(error);
        span.setAttribute('result', 'error');
      }

      span.end();
      throw error;
    }
  });
};

export const action = async ({ request, context }: ActionFunctionArgs) => {
  const tracer = trace.getTracer(OTEL_NAMES.ROUTE_TRACER);

  return tracer.startActiveSpan('login-action', async (span) => {
    try {
      span.setAttribute('route', 'anonymous.login');
      const logger = routeLogger.child('action');
      const ENV = context.cloudflare.env;
      const formData = await request.formData();
      const url = new URL(request.url);
      const cookieDomain = getCookieDomain({ href: url.href });

      const loginResponse = await tracer.startActiveSpan(
        'portal-service-login',
        async (loginSpan) => {
          const username = String(formData.get('username'));
          const password = String(formData.get('password'));

          loginSpan.setAttribute('login.username', username);
          loginSpan.setAttribute('login.password_length', password.length ?? 0);
          try {
            const result = await ENV.PORTAL_SERVICE.login(username, password, {
              cookieOptions: { domain: cookieDomain },
            });
            loginSpan.setAttribute('login.success', result.success);
            loginSpan.end();

            return result;
          } catch (error) {
            loginSpan.recordException(error);
            loginSpan.setAttribute('login.success', false);
            loginSpan.end();
            return {
              success: false,
              reason: 'SERVICE_ERROR',
            } as const;
          }
        }
      );
      logger.debug('loginResponse', loginResponse);
      if (loginResponse.success) {
        logger.debug('successful login, redirecting to homepage');
        span.setAttribute('result', 'login-success');
        span.end();
        return redirect('/', {
          headers: loginResponse.value.responseHeaders,
        });
      }
      logger.error(loginResponse.reason);
      span.setAttribute('result', loginResponse.reason);

      // TFA redirects
      if (loginResponse.reason === 'TFA_SETUP_REQUIRED') {
        span.end();
        return redirect('/tfa-setup', {
          headers: loginResponse.value.responseHeaders,
        });
      }
      if (loginResponse.reason === 'TFA_REQUIRED') {
        span.end();
        return redirect('/tfa', {
          headers: loginResponse.value.responseHeaders,
        });
      }

      // Username error displays as helper text on username field
      if (loginResponse.reason === 'INVALID_USERNAME_LENGTH') {
        span.end();
        return { username: 'Username must be between 3 and 64 characters' };
      }

      // Login error messages
      const loginErrorMessage = getLoginErrorMessage(loginResponse.reason);

      span.end();
      return { loginErrorMessage };
    } catch (error) {
      // Don't add tracing for redirect errors as they're expected flow
      if (
        !(
          error instanceof Response &&
          error.status >= 300 &&
          error.status < 400
        )
      ) {
        span.recordException(error);
        span.setAttribute('result', 'error');
      }
      span.end();
      throw error;
    }
  });
};

type LoginErrorReason = Extract<
  Awaited<ReturnType<PortalService['login']>>,
  { success: false }
>['reason'];

function getLoginErrorMessage(reason: LoginErrorReason | 'SERVICE_ERROR') {
  // Login error messages
  switch (reason) {
    case 'ACCOUNT_DISABLED':
      return 'Account deactivated. Contact admin to regain access.';
    case 'INVALID_CREDENTIALS':
    case 'USERNAME_NOT_FOUND':
      return 'Invalid username or password'; // should be localized
    default:
      return 'An error has occurred.'; // should be localized
  }
}

export function ErrorBoundary() {
  const error = useRouteError();
  const logger = routeLogger.child('ErrorBoundary');
  logger.error(error);

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: COLORS.white } }} />
      <h1>An unexpected error occurred</h1>
      <p>
        It may work if you <a href="">try again</a>. Otherwise, go back to the{' '}
        <a href="/">home page</a>
      </p>
      {error instanceof Error ? (
        <>
          <hr />
          <small>
            <details>
              <summary>error details</summary>
              <pre>{error.stack}</pre>
            </details>
          </small>
        </>
      ) : null}
    </>
  );
}

export default function Login() {
  const logger = routeLogger.child('component');
  const location = useLocation();
  const actionData = useActionData<typeof action>();

  const actionUrl = `${location.pathname}${location.search}`;
  const params = Object.fromEntries(new URLSearchParams(location.search));
  logger.debug('data', { actionData, actionUrl, params, location });
  // TODO do we need deleteCachedEntries() here?

  const signInProps: SignInProps = {
    action: actionUrl,
    redirectTo: params.redirectTo,
  };

  if (actionData) {
    if (actionData.loginErrorMessage) {
      logger.error(actionData.loginErrorMessage);
      signInProps.errors = {
        login: actionData.loginErrorMessage,
      };
    }
    if (actionData.username) {
      signInProps.errors = {
        username: actionData.username,
      };
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingX: 3,
        paddingY: 4,
        backgroundColor: 'background.paper',
        borderRadius: '10px',
      }}
    >
      <SignIn {...signInProps} />
    </Box>
  );
}
